import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Use relative URLs for API calls - works on any domain (preview or production)
const API_BASE = '/api';

// reCAPTCHA site key (v3) - only enabled on production domain
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const isProductionDomain = typeof window !== 'undefined' && 
  (window.location.hostname === 'westexlegal.com' || window.location.hostname === 'www.westexlegal.com');

// LocalStorage key for auto-save
const FORM_STORAGE_KEY = 'westex_job_request_draft';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

// Coverage data constants - moved outside component to avoid re-creation
const ALL_LUBBOCK_TOWNS = {
  zone1: ["Lubbock", "Wolfforth", "Shallowater", "Idalou", "Slaton", "Smyer", "Buffalo Springs Lake", "New Deal", "Ransom Canyon"],
  zone2: ["Abernathy", "Ropesville", "Lorenzo", "Anton", "Levelland", "Ralls", "Petersburg", "Tahoka", "New Home", "Meadow", "Southland"],
  zone3: ["Littlefield", "Crosbyton", "Hale Center", "Brownfield", "Post", "Plainview", "Sudan", "Olton", "Whiteface", "Whitharral", "Spade", "Fieldton", "Wellman", "O'Donnell", "Sundown", "Amherst"],
  zone4: ["Morton", "Lockney", "Seagraves", "Lamesa", "Kress", "Muleshoe", "Plains", "Justiceburg", "Dickens", "Spur", "Edmonson", "Welch", "Loop", "Floydada", "Earth", "Hart", "Springlake"],
  zone5: ["Dimmitt", "Denver City", "Seminole", "Snyder", "Nazareth", "Tulia"],
  zone6: ["Farwell", "Bovina", "Friona", "Andrews"]
};

const ALL_CHILDRESS_TOWNS = {
  zone1: ["Childress", "Estelline"],
  zone2: ["Memphis", "Quanah", "Wellington", "Paducah", "Newlin", "Dodson"],
  zone3: ["Crowell", "Turkey", "Chillicothe", "Clarendon", "Shamrock", "Vernon", "Quitaque", "Lakeview"],
  zone4: ["Roaring Springs", "Silverton", "Benjamin", "Guthrie", "Matador", "Wheeler"],
  zone5: ["Goree", "Munday", "Briscoe"]
};

const LUBBOCK_ZONE_PRICING = {
  zone1: { routine: 75, rush: 100 },
  zone2: { routine: 100, rush: 130 },
  zone3: { routine: 125, rush: 160 },
  zone4: { routine: 150, rush: 195 },
  zone5: { routine: 175, rush: 225 },
  zone6: { routine: 200, rush: 255 }
};

const CHILDRESS_ZONE_PRICING = {
  zone1: { routine: 95, rush: 125 },
  zone2: { routine: 125, rush: 155 },
  zone3: { routine: 155, rush: 185 },
  zone4: { routine: 185, rush: 215 },
  zone5: { routine: 215, rush: 245 }
};

const MAX_ADDRESSES = 5;
const EMPTY_ADDRESS = {
  street: '',
  city: '',
  state: 'TX',
  zip: '',
  county: ''
};

function JobRequestNew({ hideExistingClientBanner = false, prefillUser = null } = {}) {
  const navigate = useNavigate();
  
  // Form state
  const [files, setFiles] = useState([]);
  
  // Multi-address support (max 5)
  const [serviceAddresses, setServiceAddresses] = useState([{ ...EMPTY_ADDRESS }]);
  
  const [formData, setFormData] = useState({
    // Client info
    clientLawfirm: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    clientRefNumber: '',
    
    // Court case
    caseNumber: '',
    plaintiff: '',
    defendant: '',
    courtDate: '',
    courtName: '',
    courtState: 'TX',
    courtCounty: '',
    
    // Service type
    serviceType: 'routine', // routine or rush
    
    // Recipient
    recipientType: 'individual', // individual or business
    registeredAgent: '',
    partyToServe: '',
    recipientEmail: '',
    recipientPhone: '',
    
    // Descriptors
    age: '',
    gender: '',
    ethnicity: '',
    weight: '',
    height: '',
    hairColor: '',
    otherDetails: '',
    
    // Additional
    specialInstructions: ''
  });
  
  const [uploading, setUploading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [addressPricing, setAddressPricing] = useState([null]); // Pricing for each address
  const [errors, setErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const recaptchaLoaded = useRef(false);
  const streetInputRefs = useRef([]);
  const autocompleteInstances = useRef([]);

  // Load reCAPTCHA script - ONLY on production domain
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY && isProductionDomain && !recaptchaLoaded.current) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      document.head.appendChild(script);
      recaptchaLoaded.current = true;
    }
  }, []);

  // Draft state
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftAge, setDraftAge] = useState('');
  const [draftHadFiles, setDraftHadFiles] = useState([]);

  // Check for saved draft on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(FORM_STORAGE_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        const savedAt = parsed.savedAt ? new Date(parsed.savedAt) : null;
        
        // Check if draft is older than 7 days
        if (savedAt) {
          const now = new Date();
          const daysDiff = (now - savedAt) / (1000 * 60 * 60 * 24);
          
          if (daysDiff > 7) {
            // Draft expired, delete it
            localStorage.removeItem(FORM_STORAGE_KEY);
            return;
          }
          
          // Calculate age for display
          if (daysDiff < 1) {
            const hours = Math.floor((now - savedAt) / (1000 * 60 * 60));
            setDraftAge(hours <= 1 ? 'less than an hour ago' : `${hours} hours ago`);
          } else {
            setDraftAge(`${Math.floor(daysDiff)} day${Math.floor(daysDiff) !== 1 ? 's' : ''} ago`);
          }
        }
        
        // Check if draft had files
        if (parsed.fileMetadata && parsed.fileMetadata.length > 0) {
          setDraftHadFiles(parsed.fileMetadata);
        }
        
        // Show prompt to user instead of auto-restoring
        setPendingDraft(parsed);
        setShowDraftPrompt(true);
      }
    } catch (e) {
      console.log('No draft to restore');
    }
  }, []);

  // Auto-prefill from logged-in user (only fields still empty)
  useEffect(() => {
    if (!prefillUser) return;
    setFormData(prev => ({
      ...prev,
      clientLawfirm: prev.clientLawfirm || prefillUser.company_name || '',
      firstName: prev.firstName || prefillUser.first_name || '',
      lastName: prev.lastName || prefillUser.last_name || '',
      email: prev.email || prefillUser.email || '',
      phone: prev.phone || prefillUser.phone || '',
    }));
  }, [prefillUser]);

  // Restore draft when user confirms
  const restoreDraft = () => {
    if (pendingDraft) {
      if (pendingDraft.formData) setFormData(pendingDraft.formData);
      if (pendingDraft.serviceAddresses) setServiceAddresses(pendingDraft.serviceAddresses);
      setDraftRestored(true);
      setTimeout(() => setDraftRestored(false), 3000);
    }
    setShowDraftPrompt(false);
    setPendingDraft(null);
    setDraftHadFiles([]);
  };

  // Discard draft when user declines
  const discardDraft = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
    setShowDraftPrompt(false);
    setPendingDraft(null);
    setDraftHadFiles([]);
  };

  // Check if form has any meaningful data (user-entered, not defaults)
  const hasFormData = useCallback(() => {
    // Fields that have default values - don't count these
    const defaultValues = ['TX', 'routine', 'individual'];
    
    // Check if any text field has non-default content
    const hasTextData = Object.entries(formData).some(([key, val]) => {
      if (typeof val !== 'string') return false;
      const trimmed = val.trim();
      if (trimmed === '') return false;
      // Ignore default values
      if (defaultValues.includes(trimmed)) return false;
      return true;
    });
    
    // Check if any address has content (not just default state)
    const hasAddressData = serviceAddresses.some(addr => 
      addr.street.trim() !== '' || 
      addr.city.trim() !== '' || 
      addr.zip.trim() !== '' ||
      addr.county.trim() !== ''
    );
    
    // Only save draft if there's actual form data entered
    // Files alone don't trigger a draft (since we can't restore them)
    return hasTextData || hasAddressData;
  }, [formData, serviceAddresses]);

  // Auto-save: Save to localStorage when form changes (only if there's actual data)
  useEffect(() => {
    if (!showDraftPrompt && hasFormData()) {
      // Save file metadata (can't save actual files to localStorage)
      const fileMetadata = files.map(f => ({ name: f.name, size: f.size }));
      const draft = { 
        formData, 
        serviceAddresses, 
        fileMetadata,
        savedAt: new Date().toISOString() 
      };
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(draft));
    }
  }, [formData, serviceAddresses, files, showDraftPrompt, hasFormData]);

  // Clear form and draft
  const clearForm = () => {
    if (window.confirm('Are you sure you want to clear the form? All entered data will be lost.')) {
      localStorage.removeItem(FORM_STORAGE_KEY);
      setFormData({
        clientLawfirm: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        clientRefNumber: '',
        caseNumber: '',
        plaintiff: '',
        defendant: '',
        courtDate: '',
        courtName: '',
        courtState: 'TX',
        courtCounty: '',
        serviceType: 'routine',
        recipientType: 'individual',
        registeredAgent: '',
        partyToServe: '',
        recipientEmail: '',
        recipientPhone: '',
        age: '',
        gender: '',
        ethnicity: '',
        weight: '',
        height: '',
        hairColor: '',
        otherDetails: '',
        specialInstructions: ''
      });
      setServiceAddresses([{ ...EMPTY_ADDRESS }]);
      setFiles([]);
      setErrors({});
    }
  };

  // Clear draft on successful submission
  const clearDraft = () => {
    localStorage.removeItem(FORM_STORAGE_KEY);
  };

  // Get reCAPTCHA token - only on production
  const getRecaptchaToken = async () => {
    if (!RECAPTCHA_SITE_KEY || !isProductionDomain || !window.grecaptcha) {
      return null; // reCAPTCHA not configured or not on production, skip
    }
    try {
      await window.grecaptcha.ready(() => {});
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit_job' });
      return token;
    } catch (e) {
      console.error('reCAPTCHA error:', e);
      return null;
    }
  };

  // Calculate pricing for a specific address
  const calculatePricingForAddress = useCallback((city, state) => {
    if (!city || !city.trim()) return null;
    
    const trimmedCity = city.trim();
    
    // Check if city is in coverage area
    let found = false;
    let hub = '';
    let zone = '';
    let prices = null;

    // Check Lubbock zones
    for (const [zoneKey, cities] of Object.entries(ALL_LUBBOCK_TOWNS)) {
      if (cities.some(c => c.toLowerCase() === trimmedCity.toLowerCase())) {
        hub = 'Lubbock';
        zone = zoneKey;
        prices = LUBBOCK_ZONE_PRICING[zoneKey];
        found = true;
        break;
      }
    }

    // Check Childress zones if not found
    if (!found) {
      for (const [zoneKey, cities] of Object.entries(ALL_CHILDRESS_TOWNS)) {
        if (cities.some(c => c.toLowerCase() === trimmedCity.toLowerCase())) {
          hub = 'Childress';
          zone = zoneKey;
          prices = CHILDRESS_ZONE_PRICING[zoneKey];
          found = true;
          break;
        }
      }
    }

    if (found && prices) {
      return {
        displayCity: `${trimmedCity}, ${state}`,
        hub,
        zone: zone.replace('zone', 'Zone '),
        routine: prices.routine,
        rush: prices.rush,
        isExactCity: true
      };
    } else if (state === 'TX' || state === 'NM') {
      return {
        displayCity: `${trimmedCity}, ${state}`,
        hub: 'Lubbock',
        zone: 'Coverage Area',
        routine: 'Contact',
        rush: 'Contact',
        isExactCity: false
      };
    }
    
    return null;
  }, []);

  // Calculate pricing when addresses change
  useEffect(() => {
    const newPricing = serviceAddresses.map(addr => 
      calculatePricingForAddress(addr.city, addr.state)
    );
    setAddressPricing(newPricing);
    
    // Show pricing if at least one address has valid pricing
    const hasValidPricing = newPricing.some(p => p !== null);
    setShowPricing(hasValidPricing);
    
    // Set main pricing to first address with valid pricing (for backward compat)
    setPricing(newPricing.find(p => p !== null) || null);
  }, [serviceAddresses, calculatePricingForAddress]);

  // Multi-address handlers
  const addServiceAddress = () => {
    if (serviceAddresses.length < MAX_ADDRESSES) {
      setServiceAddresses(prev => [...prev, { ...EMPTY_ADDRESS }]);
      setAddressPricing(prev => [...prev, null]);
    }
  };

  const removeServiceAddress = (index) => {
    if (serviceAddresses.length > 1) {
      setServiceAddresses(prev => prev.filter((_, i) => i !== index));
      setAddressPricing(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Reorder an address up (-1) or down (+1) — used to let the user pick
  // which address is Primary and the order of the rest.
  const moveServiceAddress = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= serviceAddresses.length) return;
    setServiceAddresses(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setAddressPricing(prev => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    // Reset autocomplete bindings — the underlying <input> elements stay,
    // but the index association we stored in autocompleteInstances no longer
    // matches. The attach-effect will rebuild on the next render.
    autocompleteInstances.current = [];
  };

  const updateServiceAddress = (index, field, value) => {
    setServiceAddresses(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear error for this field
    const errorKey = `address_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: '' }));
    }
  };

  // Bulk update service address (from autocomplete)
  const updateServiceAddressBulk = useCallback((index, patch) => {
    setServiceAddresses(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
    setErrors(prev => {
      const next = { ...prev };
      Object.keys(patch).forEach(k => { delete next[`address_${index}_${k}`]; });
      return next;
    });
  }, []);

  // Parse Google Places result into our address fields
  const parsePlaceResult = (place) => {
    if (!place || !place.address_components) return null;
    const get = (type, useShort = false) => {
      const comp = place.address_components.find(c => c.types.includes(type));
      if (!comp) return '';
      return useShort ? comp.short_name : comp.long_name;
    };
    const streetNumber = get('street_number');
    const route = get('route');
    const street = [streetNumber, route].filter(Boolean).join(' ').trim();
    const city = get('locality') || get('sublocality') || get('postal_town') || get('administrative_area_level_3');
    const state = get('administrative_area_level_1', true);
    const zip = get('postal_code');
    const countyRaw = get('administrative_area_level_2');
    const county = countyRaw ? countyRaw.replace(/\s+County$/i, '').trim() : '';
    return { street, city, state, zip, county };
  };

  // Attach Google Places Autocomplete to each street input
  useEffect(() => {
    // Reset instances when address array changes (indices may shift)
    autocompleteInstances.current = [];
    const tryAttach = () => {
      // Only run when Places library is loaded
      if (!(window.google && window.google.maps && window.google.maps.places)) return false;
      streetInputRefs.current.forEach((input, index) => {
        if (!input || autocompleteInstances.current[index]) return;
        try {
          const ac = new window.google.maps.places.Autocomplete(input, {
            types: ['address'],
            componentRestrictions: { country: 'us' },
            fields: ['address_components', 'formatted_address'],
          });
          ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            const parsed = parsePlaceResult(place);
            if (parsed && parsed.street) {
              updateServiceAddressBulk(index, parsed);
            }
          });
          autocompleteInstances.current[index] = ac;
        } catch (e) {
          // Silent — fallback to manual entry
        }
      });
      return true;
    };
    if (!tryAttach()) {
      // Places not ready yet — poll briefly
      const id = setInterval(() => { if (tryAttach()) clearInterval(id); }, 400);
      setTimeout(() => clearInterval(id), 8000);
      return () => clearInterval(id);
    }
  }, [serviceAddresses.length, updateServiceAddressBulk]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== newFiles.length) {
      alert('Only PDF files are allowed');
    }

    // Check total size
    const existingSize = files.reduce((sum, f) => sum + f.size, 0);
    const newSize = pdfFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSize = existingSize + newSize;
    const maxSize = 25 * 1024 * 1024; // 25MB

    if (totalSize > maxSize) {
      alert(`Total file size cannot exceed 25MB. Current: ${(totalSize / 1024 / 1024).toFixed(1)}MB`);
      return;
    }

    setFiles(prev => [...prev, ...pdfFiles]);
    if (errors.files) {
      setErrors(prev => ({ ...prev, files: '' }));
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields - order matters for scroll priority
    if (files.length === 0) newErrors.files = 'At least one PDF is required';
    if (!formData.clientLawfirm.trim()) newErrors.clientLawfirm = 'Client/Lawfirm name is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.caseNumber.trim()) newErrors.caseNumber = 'Case number is required';
    if (!formData.plaintiff.trim()) newErrors.plaintiff = 'Plaintiff is required';
    if (!formData.defendant.trim()) newErrors.defendant = 'Defendant is required';
    if (!formData.courtName.trim()) newErrors.courtName = 'Court name is required';
    if (!formData.courtState) newErrors.courtState = 'Court state is required';
    if (!formData.courtCounty.trim()) newErrors.courtCounty = 'Court county is required';
    if (!formData.partyToServe.trim()) newErrors.partyToServe = 'Party to serve is required';
    if (formData.recipientType === 'business' && !formData.registeredAgent.trim()) {
      newErrors.registeredAgent = 'Registered agent is required for business';
    }
    
    // Validate all service addresses
    serviceAddresses.forEach((addr, index) => {
      if (!addr.street.trim()) newErrors[`address_${index}_street`] = 'Address is required';
      if (!addr.city.trim()) newErrors[`address_${index}_city`] = 'City is required';
      if (!addr.state) newErrors[`address_${index}_state`] = 'State is required';
      if (!addr.zip.trim()) newErrors[`address_${index}_zip`] = 'ZIP code is required';
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    setErrors(newErrors);
    
    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      
      // Map error keys to element IDs or data attributes
      setTimeout(() => {
        let element = null;
        
        // Handle special cases
        if (firstErrorKey === 'files') {
          element = document.querySelector('[data-testid="file-upload-area"]');
        } else if (firstErrorKey.startsWith('address_')) {
          // Extract index and field from address_0_street format
          const parts = firstErrorKey.split('_');
          const addrIndex = parts[1];
          const field = parts[2];
          element = document.querySelector(`[data-testid="address-${addrIndex}-${field}"]`);
        } else {
          element = document.querySelector(`[data-testid="field-${firstErrorKey}"]`);
        }
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Try to focus the input if it's focusable
          const input = element.querySelector('input, select, textarea') || element;
          if (input.focus) input.focus();
        }
      }, 100);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setUploading(true);

    try {
      // Get reCAPTCHA token (if configured)
      const recaptchaToken = await getRecaptchaToken();

      // Build service_addresses array from all addresses
      const formattedAddresses = serviceAddresses.map((addr, index) => ({
        address: {
          street: addr.street,
          city: addr.city,
          state: addr.state,
          zip_code: addr.zip
        },
        address_type: index === 0 ? "primary" : "additional"
      }));

      // Step 1: Create job request
      const jobPayload = {
        client_name: `${formData.firstName} ${formData.lastName}`,
        client_email: formData.email,
        client_phone: formData.phone,
        client_company: formData.clientLawfirm,
        internal_reference: formData.clientRefNumber || undefined,
        
        case_title: `${formData.plaintiff} v. ${formData.defendant}`,
        case_number: formData.caseNumber,
        court_location: `${formData.courtName}, ${formData.courtCounty} County, ${formData.courtState}`,
        
        service_type: "personal",
        priority: formData.serviceType === 'rush' ? 'rush' : 'normal',
        service_deadline: formData.courtDate || undefined,  // Optional field
        
        recipient: {
          type: formData.recipientType,  // Required field
          name: formData.partyToServe,
          phone: formData.recipientPhone || undefined,
          // Only include physical_description if at least one field has a value
          ...(formData.age || formData.gender || formData.ethnicity || formData.weight || formData.height || formData.hairColor || formData.otherDetails ? {
            physical_description: {
              age: formData.age ? parseInt(formData.age) : undefined,
              gender: formData.gender || undefined,
              ethnicity: formData.ethnicity || undefined,
              weight: formData.weight || undefined,
              height: formData.height || undefined,
              hair: formData.hairColor || undefined,
              other_details: formData.otherDetails || undefined
            }
          } : {})
        },
        
        // Include reCAPTCHA token if available
        recaptcha_token: recaptchaToken || undefined,
        
        service_addresses: formattedAddresses,
        
        special_instructions: formData.specialInstructions || undefined,
        document_count: files.length
      };

      const jobResponse = await fetch(`${API_BASE}/jobs/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobPayload),
      });

      if (!jobResponse.ok) {
        const errorData = await jobResponse.json().catch(() => ({}));
        console.error('Job creation error details:', errorData);
        throw new Error(errorData.detail || 'Failed to create job request');
      }

      const jobData = await jobResponse.json();
      const jobId = jobData.job_id;

      // Step 2: Upload documents in small chunks.
      // A single large multipart POST can be blocked by the CDN/edge (size or
      // timeout limits), which silently failed real submissions in production.
      // Chunking keeps every request small + fast so it always reaches the API.
      const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per chunk
      const uploadSession = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const postChunkWithRetry = async (fd, attempts = 3) => {
        let lastErr;
        for (let a = 0; a < attempts; a++) {
          try {
            const r = await fetch(`${API_BASE}/jobs/${jobId}/documents/chunk`, { method: 'POST', body: fd });
            if (r.ok) return;
            lastErr = new Error(`Chunk upload failed (HTTP ${r.status})`);
          } catch (e) {
            lastErr = e;
          }
          await new Promise(res => setTimeout(res, 800 * (a + 1)));
        }
        throw lastErr || new Error('Chunk upload failed');
      };

      for (let fi = 0; fi < files.length; fi++) {
        const file = files[fi];
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        for (let ci = 0; ci < totalChunks; ci++) {
          const blob = file.slice(ci * CHUNK_SIZE, (ci + 1) * CHUNK_SIZE);
          const fd = new FormData();
          fd.append('upload_session', uploadSession);
          fd.append('file_index', String(fi));
          fd.append('chunk_index', String(ci));
          fd.append('total_chunks', String(totalChunks));
          fd.append('filename', file.name);
          fd.append('content_type', file.type || 'application/pdf');
          fd.append('chunk', blob, file.name);
          await postChunkWithRetry(fd);
        }
      }

      // Assemble + email on the server
      const finalizeFd = new FormData();
      finalizeFd.append('upload_session', uploadSession);
      const uploadResponse = await fetch(`${API_BASE}/jobs/${jobId}/documents/finalize`, {
        method: 'POST',
        body: finalizeFd,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload documents');
      }

      // Clear the saved draft on successful submission
      clearDraft();

      // Success - navigate to success page with all data for PDF receipt
      navigate('/job-request-success', {
        state: {
          jobId: jobId,
          email: formData.email,
          clientName: `${formData.firstName} ${formData.lastName}`,
          clientCompany: formData.clientLawfirm,
          clientPhone: formData.phone,
          clientRef: formData.clientRefNumber,
          caseTitle: `${formData.plaintiff} v. ${formData.defendant}`,
          caseNumber: formData.caseNumber,
          courtLocation: `${formData.courtName}, ${formData.courtCounty} County, ${formData.courtState}`,
          courtDate: formData.courtDate,
          priority: formData.serviceType === 'rush' ? 'Rush' : 'Routine',
          recipientType: formData.recipientType,
          partyToServe: formData.partyToServe,
          files: files.map(f => ({ name: f.name, size: f.size })),
          addresses: serviceAddresses,
          addressCount: serviceAddresses.length,
          pricing: pricing,
          submittedAt: new Date().toISOString()
        }
      });

    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your request. Please try again or contact us at (806) 401-1749.');
    } finally {
      setUploading(false);
    }
  };

  const totalFileSize = files.reduce((sum, f) => sum + f.size, 0);
  const maxSize = 25 * 1024 * 1024;
  const sizePercentage = (totalFileSize / maxSize) * 100;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Background gradient for entire page */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-red-950/20 to-black pointer-events-none"></div>
      
      {/* Draft Restore Prompt Modal - Fixed at top */}
      {showDraftPrompt && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-md mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-600/20 rounded-full">
                <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Continue Previous Draft?</h3>
            </div>
            <p className="text-gray-300 mb-2">
              You have a saved draft from <span className="text-white font-medium">{draftAge}</span>.
            </p>
            <p className="text-gray-400 text-sm mb-4">
              Would you like to continue where you left off, or start fresh?
            </p>
            
            {/* File re-upload warning */}
            {draftHadFiles.length > 0 && (
              <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="text-amber-300 text-sm font-medium">Documents need to be re-uploaded</p>
                    <p className="text-amber-200/70 text-xs mt-1">
                      {draftHadFiles.length} file{draftHadFiles.length !== 1 ? 's' : ''} previously uploaded:
                    </p>
                    <ul className="text-amber-200/70 text-xs mt-1">
                      {draftHadFiles.map((file, i) => (
                        <li key={i}>• {file.name}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={restoreDraft}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Continue Draft
              </button>
              <button
                onClick={discardDraft}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft Restored Notification */}
      {draftRestored && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">Draft restored successfully</span>
          <button onClick={() => setDraftRestored(false)} className="ml-2 hover:text-green-200">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      <div className="relative z-10">
        {/* Header matching other pages */}
        <section className="relative py-20 overflow-hidden">
          <div className="relative container mx-auto px-4 text-center">
            <h1 className="heading-accent font-serif-head text-3xl md:text-4xl font-bold text-white mb-6">
              Request Process Service
            </h1>
            <div className="relative w-full max-w-2xl mx-auto h-px mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-600 to-transparent"></div>
            </div>
          </div>
        </section>

        {/* Existing Client Banner */}
        {!hideExistingClientBanner && (
        <div className="relative pb-4">
          <div className="relative max-w-4xl mx-auto px-6">
            <a 
              href="/portal/login" 
              className="block bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-700/40 rounded-lg p-4 hover:from-blue-900/60 hover:to-indigo-900/60 transition-all group"
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600/20 rounded-full">
                    <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Existing client?</p>
                    <p className="text-blue-200/80 text-xs">Log in to your portal for faster submissions & job tracking</p>
                  </div>
                </div>
                <span className="text-blue-400 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Log In
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </a>
          </div>
        </div>
        )}

        {/* Warning Banner - No background */}
        <div className="relative pb-8">
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="text-center">
              <h3 className="text-base font-semibold text-white mb-2">Important: One Defendant Per Submission</h3>
              <p className="text-sm text-gray-300 mb-2 leading-relaxed">
                Each person or entity being served requires a separate job request.
              </p>
              <p className="text-xs text-gray-400 italic">
                Example: Serving &quot;John Smith individually&quot; AND &quot;ABC Company c/o John Smith&quot; requires 2 separate submissions.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Clear Form Button - Top */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={clearForm}
              className="px-6 py-2.5 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 rounded-lg font-semibold text-sm transition-all shadow-lg flex items-center gap-2"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear Form
            </button>
          </div>
          
          {/* Documents Upload Section */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-2 text-white">Upload Documents</h2>
              <p className="text-sm text-gray-400 mb-6">PDF files only, maximum 25MB total</p>
            
            <div
              data-testid="file-upload-area"
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                errors.files ? 'border-red-500 bg-red-900/10' : isDragging ? 'border-red-500 bg-red-900/20' : 'border-gray-600 hover:border-red-500'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-2 text-sm text-gray-300">
                <label htmlFor="file-upload" className="cursor-pointer text-red-500 hover:text-red-400">
                  Click to upload
                </label>
                {' '}or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1">PDF files only</p>
              <input
                id="file-upload"
                type="file"
                className="hidden"
                accept=".pdf"
                multiple
                onChange={handleFileChange}
              />
            </div>

            {files.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Total Size: {(totalFileSize / 1024 / 1024).toFixed(2)} MB / 25 MB</span>
                  <span className="text-gray-400">{files.length} file(s)</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
                  <div 
                    className={`h-2 rounded-full transition-all ${sizePercentage > 90 ? 'bg-red-500' : 'bg-red-600'}`}
                    style={{ width: `${Math.min(sizePercentage, 100)}%` }}
                  ></div>
                </div>
                <ul className="space-y-2">
                  {files.map((file, index) => (
                    <li key={index} className="flex items-center justify-between bg-gray-800 p-3 rounded">
                      <div className="flex items-center flex-1">
                        <svg className="h-5 w-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm truncate">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="ml-4 text-red-500 hover:text-red-400"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {errors.files && <p className="text-red-500 text-sm mt-2">{errors.files}</p>}
            </div>
          </div>

          {/* Client Information */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-6 text-white">Client Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2" data-testid="field-clientLawfirm">
                <label className="block text-sm font-medium mb-2">Company Name *</label>
                <input
                  type="text"
                  name="clientLawfirm"
                  value={formData.clientLawfirm}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.clientLawfirm ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Smith Law Firm"
                />
                {errors.clientLawfirm && <p className="text-red-500 text-sm mt-1">{errors.clientLawfirm}</p>}
              </div>
              
              <div data-testid="field-firstName">
                <label className="block text-sm font-medium mb-2">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.firstName ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="John"
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
              </div>
              
              <div data-testid="field-lastName">
                <label className="block text-sm font-medium mb-2">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.lastName ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Smith"
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
              </div>
              
              <div data-testid="field-email">
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.email ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="john@smithlaw.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              
              <div data-testid="field-phone">
                <label className="block text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.phone ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="(806) 555-1234"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Internal Reference Number</label>
                <input
                  type="text"
                  name="clientRefNumber"
                  value={formData.clientRefNumber}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                  placeholder="Optional reference number"
                />
              </div>
            </div>
            </div>
          </div>

          {/* Court Case Information */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-6 text-white">Court Case Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="field-caseNumber">
                <label className="block text-sm font-medium mb-2">Case Number *</label>
                <input
                  type="text"
                  name="caseNumber"
                  value={formData.caseNumber}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.caseNumber ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="CV-2024-001234"
                />
                {errors.caseNumber && <p className="text-red-500 text-sm mt-1">{errors.caseNumber}</p>}
              </div>
              
              <div data-testid="field-plaintiff">
                <label className="block text-sm font-medium mb-2">Plaintiff *</label>
                <input
                  type="text"
                  name="plaintiff"
                  value={formData.plaintiff}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.plaintiff ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="John Doe"
                />
                {errors.plaintiff && <p className="text-red-500 text-sm mt-1">{errors.plaintiff}</p>}
              </div>
              
              <div data-testid="field-defendant">
                <label className="block text-sm font-medium mb-2">Defendant *</label>
                <input
                  type="text"
                  name="defendant"
                  value={formData.defendant}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.defendant ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Jane Smith"
                />
                {errors.defendant && <p className="text-red-500 text-sm mt-1">{errors.defendant}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Court Date (if any)</label>
                <input
                  type="date"
                  name="courtDate"
                  value={formData.courtDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                />
              </div>
              
              <div className="md:col-span-2" data-testid="field-courtName">
                <label className="block text-sm font-medium mb-2">Court Name *</label>
                <input
                  type="text"
                  name="courtName"
                  value={formData.courtName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.courtName ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="99th District Court"
                />
                {errors.courtName && <p className="text-red-500 text-sm mt-1">{errors.courtName}</p>}
              </div>
              
              <div data-testid="field-courtState">
                <label className="block text-sm font-medium mb-2">Court Location *</label>
                <select
                  name="courtState"
                  value={formData.courtState}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.courtState ? 'border-red-500' : 'border-gray-600'}`}
                >
                  {US_STATES.map(state => (
                    <option key={state.code} value={state.code}>{state.name}</option>
                  ))}
                </select>
                {errors.courtState && <p className="text-red-500 text-sm mt-1">{errors.courtState}</p>}
              </div>
              
              <div data-testid="field-courtCounty">
                <label className="block text-sm font-medium mb-2">County *</label>
                <input
                  type="text"
                  name="courtCounty"
                  value={formData.courtCounty}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.courtCounty ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Lubbock"
                />
                {errors.courtCounty && <p className="text-red-500 text-sm mt-1">{errors.courtCounty}</p>}
              </div>
            </div>
            </div>
          </div>

          {/* Service Type */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-6 text-white">Service Type</h2>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="serviceType"
                  value="routine"
                  checked={formData.serviceType === 'routine'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 focus:ring-red-500"
                />
                <span className="ml-2">Routine Service</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="serviceType"
                  value="rush"
                  checked={formData.serviceType === 'rush'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 focus:ring-red-500"
                />
                <span className="ml-2">Rush Service</span>
              </label>
            </div>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-6 text-white">Recipient / Service Information</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Recipient Type *</label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="recipientType"
                    value="individual"
                    checked={formData.recipientType === 'individual'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 focus:ring-red-500"
                  />
                  <span className="ml-2">Individual</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="recipientType"
                    value="business"
                    checked={formData.recipientType === 'business'}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-red-600 bg-gray-800 border-gray-600 focus:ring-red-500"
                  />
                  <span className="ml-2">Business</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-2">💡 Reminder: One defendant per submission. Serving multiple people? Submit separate forms.</p>
            </div>

            {formData.recipientType === 'business' && (
              <div className="mb-4" data-testid="field-partyToServe">
                <label className="block text-sm font-medium mb-2">Party to Serve *</label>
                <input
                  type="text"
                  name="partyToServe"
                  value={formData.partyToServe}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.partyToServe ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Full name of person/entity to be served"
                />
                {errors.partyToServe && <p className="text-red-500 text-sm mt-1">{errors.partyToServe}</p>}
              </div>
            )}

            {formData.recipientType === 'business' && (
              <div className="mb-4" data-testid="field-registeredAgent">
                <label className="block text-sm font-medium mb-2">Registered Agent *</label>
                <input
                  type="text"
                  name="registeredAgent"
                  value={formData.registeredAgent}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.registeredAgent ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Name of registered agent"
                />
                {errors.registeredAgent && <p className="text-red-500 text-sm mt-1">{errors.registeredAgent}</p>}
              </div>
            )}

            {formData.recipientType === 'individual' && (
              <div className="mb-4" data-testid="field-partyToServe">
                <label className="block text-sm font-medium mb-2">Party to Serve *</label>
                <input
                  type="text"
                  name="partyToServe"
                  value={formData.partyToServe}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors.partyToServe ? 'border-red-500' : 'border-gray-600'}`}
                  placeholder="Full name of person/entity to be served"
                />
                {errors.partyToServe && <p className="text-red-500 text-sm mt-1">{errors.partyToServe}</p>}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="recipientEmail"
                  value={formData.recipientEmail}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                  placeholder="recipient@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="recipientPhone"
                  value={formData.recipientPhone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                  placeholder="(806) 555-1234"
                />
              </div>
            </div>

            {/* Descriptors */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Physical Descriptors (Optional)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Age</label>
                  <input
                    type="text"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                    placeholder="35"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                  >
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Ethnicity</label>
                  <input
                    type="text"
                    name="ethnicity"
                    value={formData.ethnicity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                    placeholder="Caucasian"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                    placeholder="180 lbs"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Height</label>
                  <input
                    type="text"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                    placeholder="5'10&quot;"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Hair Color</label>
                  <input
                    type="text"
                    name="hairColor"
                    value={formData.hairColor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                    placeholder="Brown"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">Other Details (Vehicle, etc.)</label>
                <textarea
                  name="otherDetails"
                  value={formData.otherDetails}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                  placeholder="Blue Toyota Camry, works at XYZ Company, etc."
                ></textarea>
              </div>
            </div>
            </div>
          </div>

          {/* Service Addresses - Multi-address support */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Service Address(es)</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    {serviceAddresses.length > 1 && (
                      <span className="text-amber-400 font-medium">
                        Note: Each additional address incurs a separate service fee.
                      </span>
                    )}
                  </p>
                </div>
                <span className="text-sm text-gray-400">
                  {serviceAddresses.length} / {MAX_ADDRESSES} addresses
                </span>
              </div>

              {/* Multiple Address Cards */}
              <div className="space-y-6">
                {serviceAddresses.map((addr, index) => (
                  <div 
                    key={index} 
                    className={`relative p-6 rounded-lg border ${
                      index === 0 
                        ? 'bg-gray-800/50 border-red-600/30' 
                        : 'bg-gray-800/30 border-gray-600'
                    }`}
                  >
                    {/* Address Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          index === 0 
                            ? 'bg-red-600/20 text-red-400' 
                            : 'bg-gray-700 text-gray-300'
                        }`}>
                          {index === 0 ? 'Primary Address' : `Address ${index + 1}`}
                        </span>
                        {addressPricing[index] && (
                          <span className="px-2 py-1 rounded text-xs bg-green-600/20 text-green-400">
                            {addressPricing[index].hub} - {addressPricing[index].zone}
                          </span>
                        )}
                      </div>
                      {serviceAddresses.length > 1 && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => moveServiceAddress(index, -1)}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move up"
                            aria-label="Move address up in priority"
                            data-testid={`address-${index}-move-up`}
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => moveServiceAddress(index, 1)}
                            disabled={index === serviceAddresses.length - 1}
                            className="p-1 text-gray-400 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move down"
                            aria-label="Move address down in priority"
                            data-testid={`address-${index}-move-down`}
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => removeServiceAddress(index)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove address"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Address Fields */}
                    <div className="grid grid-cols-1 gap-4">
                      <div data-testid={`address-${index}-street`}>
                        <label className="block text-sm font-medium mb-2">Street Address *</label>
                        <input
                          type="text"
                          ref={(el) => { streetInputRefs.current[index] = el; }}
                          value={addr.street}
                          onChange={(e) => updateServiceAddress(index, 'street', e.target.value)}
                          className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors[`address_${index}_street`] ? 'border-red-500' : 'border-gray-600'}`}
                          placeholder="Start typing address…"
                          autoComplete="off"
                        />
                        {errors[`address_${index}_street`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`address_${index}_street`]}</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div data-testid={`address-${index}-city`}>
                          <label className="block text-sm font-medium mb-2">City *</label>
                          <input
                            type="text"
                            value={addr.city}
                            onChange={(e) => updateServiceAddress(index, 'city', e.target.value)}
                            className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors[`address_${index}_city`] ? 'border-red-500' : 'border-gray-600'}`}
                            placeholder="Lubbock"
                          />
                          {errors[`address_${index}_city`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`address_${index}_city`]}</p>
                          )}
                        </div>
                        
                        <div data-testid={`address-${index}-state`}>
                          <label className="block text-sm font-medium mb-2">State *</label>
                          <select
                            value={addr.state}
                            onChange={(e) => updateServiceAddress(index, 'state', e.target.value)}
                            className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors[`address_${index}_state`] ? 'border-red-500' : 'border-gray-600'}`}
                          >
                            {US_STATES.map(state => (
                              <option key={state.code} value={state.code}>{state.code}</option>
                            ))}
                          </select>
                          {errors[`address_${index}_state`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`address_${index}_state`]}</p>
                          )}
                        </div>
                        
                        <div data-testid={`address-${index}-zip`}>
                          <label className="block text-sm font-medium mb-2">ZIP *</label>
                          <input
                            type="text"
                            value={addr.zip}
                            onChange={(e) => updateServiceAddress(index, 'zip', e.target.value)}
                            className={`w-full px-4 py-2 bg-gray-800 border rounded-lg focus:outline-none focus:border-red-500 ${errors[`address_${index}_zip`] ? 'border-red-500' : 'border-gray-600'}`}
                            placeholder="79401"
                          />
                          {errors[`address_${index}_zip`] && (
                            <p className="text-red-500 text-sm mt-1">{errors[`address_${index}_zip`]}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">County</label>
                          <input
                            type="text"
                            value={addr.county}
                            onChange={(e) => updateServiceAddress(index, 'county', e.target.value)}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                            placeholder="Lubbock"
                          />
                        </div>
                      </div>

                      {/* Pricing display for this address */}
                      {addressPricing[index] && (
                        <div className="mt-2 p-3 bg-gray-900/50 rounded-lg border border-green-600/20">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-300">
                              <span className="text-green-400 font-medium">{addressPricing[index].displayCity}</span>
                              {' '}- {addressPricing[index].hub} {addressPricing[index].zone}
                            </span>
                            <div className="flex gap-4 text-sm">
                              <span className="text-gray-400">
                                Routine: <span className="text-white font-medium">
                                  {typeof addressPricing[index].routine === 'number' 
                                    ? `$${addressPricing[index].routine}` 
                                    : addressPricing[index].routine}
                                </span>
                              </span>
                              <span className="text-gray-400">
                                Rush: <span className="text-white font-medium">
                                  {typeof addressPricing[index].rush === 'number' 
                                    ? `$${addressPricing[index].rush}` 
                                    : addressPricing[index].rush}
                                </span>
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Address Button */}
              {serviceAddresses.length < MAX_ADDRESSES && (
                <button
                  type="button"
                  onClick={addServiceAddress}
                  className="mt-6 w-full py-3 px-4 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-red-500 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
                  data-testid="add-address-btn"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Another Address ({serviceAddresses.length}/{MAX_ADDRESSES})
                </button>
              )}

              {/* Multi-address fee notice */}
              {serviceAddresses.length > 1 && (
                <div className="mt-4 p-4 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-amber-300 font-medium text-sm">Multiple Address Notice</p>
                      <p className="text-amber-200/80 text-sm mt-1">
                        You have {serviceAddresses.length} addresses. Each address will incur a separate service fee. 
                        We&apos;ll contact you to confirm total pricing before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Final pricing note */}
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-400">
                <svg className="h-4 w-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>Final pricing may vary based on printing fees and service complexity.</p>
              </div>

              {/* Estimated Total - subtle, only shows when we have valid pricing */}
              {(() => {
                const validPrices = addressPricing.filter(p => p && typeof p.routine === 'number');
                if (validPrices.length === 0) return null;
                
                const routineTotal = validPrices.reduce((sum, p) => sum + p.routine, 0);
                const rushTotal = validPrices.reduce((sum, p) => sum + p.rush, 0);
                const hasContactPricing = addressPricing.some(p => p && p.routine === 'Contact');
                
                return (
                  <div className="mt-4 pt-4 border-t border-gray-700/50">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">
                        Estimated Total ({validPrices.length} address{validPrices.length !== 1 ? 'es' : ''})
                        {hasContactPricing && <span className="text-amber-400 text-xs ml-1">+ contact for quote</span>}
                      </span>
                      <div className="flex gap-4">
                        <span className="text-gray-300">
                          {formData.serviceType === 'routine' ? (
                            <span className="font-medium text-white">${routineTotal}</span>
                          ) : (
                            <span className="text-gray-500">${routineTotal}</span>
                          )}
                          <span className="text-gray-500 text-xs ml-1">routine</span>
                        </span>
                        <span className="text-gray-300">
                          {formData.serviceType === 'rush' ? (
                            <span className="font-medium text-white">${rushTotal}</span>
                          ) : (
                            <span className="text-gray-500">${rushTotal}</span>
                          )}
                          <span className="text-gray-500 text-xs ml-1">rush</span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Special Instructions */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-800 rounded-lg blur opacity-20 group-hover:opacity-30 transition duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg p-8 border border-gray-700">
              <h2 className="text-xl font-bold mb-6 text-white">Additional Information</h2>
            <div>
              <label className="block text-sm font-medium mb-2">Special Instructions</label>
              <textarea
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-red-500"
                placeholder="Any special instructions or notes for the process server..."
              ></textarea>
            </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center gap-6 mt-12">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600 rounded-lg blur opacity-50 group-hover:opacity-75 transition duration-300"></div>
              <button
                type="submit"
                disabled={uploading}
                className="relative px-12 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-700 disabled:to-gray-800 rounded-lg font-bold text-base transition-all disabled:cursor-not-allowed shadow-2xl"
              >
                {uploading ? (
                  <span className="flex items-center gap-3">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting Request...
                  </span>
                ) : (
                  'Submit Job Request'
                )}
              </button>
              
              {/* Clear Form Button - Bottom (matching style) */}
            </div>
            
            <p className="text-sm text-gray-400">
              Questions? Call <a href="tel:8064011749" className="text-red-500 hover:text-red-400 font-semibold transition-colors">(806) 401-1749</a>
            </p>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default JobRequestNew;
